import { useEffect, useState, type FormEvent } from "react";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Label } from "../../components/ui/Label";
import { Select } from "../../components/ui/Select";
import { Sheet } from "../../components/ui/Sheet";
import { Badge } from "../../components/ui/Badge";
import { api, ApiException } from "../../lib/api";

interface Member {
  id: string;
  email: string;
  name: string | null;
  role: "admin" | "member";
  joined_at: string;
}

interface PendingInvite {
  id: string;
  email: string;
  role: "admin" | "member";
  expires_at: string;
}

interface MembersResponse {
  members: Member[];
  pending_invites: PendingInvite[];
}

export function MembersPanel({ circleId, currentUserId }: { circleId: string; currentUserId: string }) {
  const [data, setData] = useState<MembersResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member">("member");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  function refresh() {
    return api.get<MembersResponse>(`/api/circles/${circleId}/members`).then(setData);
  }

  useEffect(() => {
    refresh().catch(() => {});
  }, [circleId]);

  async function submitInvite(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/circles/${circleId}/members`, { email: inviteEmail, role: inviteRole });
      setInfo(`Invite sent to ${inviteEmail}.`);
      setInviteEmail("");
      setInviteRole("member");
      setInviteOpen(false);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Failed to send invite.");
    } finally {
      setBusy(false);
    }
  }

  async function changeRole(userId: string, role: "admin" | "member") {
    setError(null);
    try {
      await api.post(`/api/circles/${circleId}/members/${userId}`, { role });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Failed to update role.");
    }
  }

  async function remove(userId: string, email: string) {
    if (!confirm(`Remove ${email} from this circle?`)) return;
    setError(null);
    try {
      await api.del(`/api/circles/${circleId}/members/${userId}`);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Failed to remove member.");
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Members</h2>
        <Button onClick={() => setInviteOpen(true)}>Invite</Button>
      </div>

      {info && (
        <div className="mt-3 rounded-md bg-green-50 border border-green-200 text-success text-sm px-3 py-2">{info}</div>
      )}
      {error && (
        <div className="mt-3 rounded-md bg-red-50 border border-red-200 text-danger text-sm px-3 py-2">{error}</div>
      )}

      <ul className="mt-4 divide-y divide-border">
        {data?.members.map((m) => (
          <li key={m.id} className="py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-medium truncate">{m.name || m.email}</div>
              {m.name && <div className="text-sm text-text-muted truncate">{m.email}</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge tone={m.role === "admin" ? "info" : "neutral"}>{m.role}</Badge>
              {m.id !== currentUserId && (
                <>
                  <Select
                    value={m.role}
                    onChange={(e) => changeRole(m.id, e.target.value as "admin" | "member")}
                    className="h-9 w-28 text-sm"
                  >
                    <option value="member">member</option>
                    <option value="admin">admin</option>
                  </Select>
                  <Button variant="ghost" size="md" onClick={() => remove(m.id, m.email)} className="text-danger">
                    Remove
                  </Button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>

      {data && data.pending_invites.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-text-muted uppercase tracking-wide">Pending invites</h3>
          <ul className="mt-2 divide-y divide-border">
            {data.pending_invites.map((i) => (
              <li key={i.id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate">{i.email}</div>
                  <div className="text-xs text-text-muted">expires {new Date(i.expires_at).toLocaleString()}</div>
                </div>
                <Badge tone="warning">pending</Badge>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Sheet
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Invite a family member"
        description="They'll get an email with a one-click link to accept."
      >
        <form onSubmit={submitInvite} className="space-y-4">
          <div>
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              required
              placeholder="brother@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="invite-role">Role</Label>
            <Select
              id="invite-role"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as "admin" | "member")}
            >
              <option value="member">Member — can accept visits</option>
              <option value="admin">Admin — can also create visits and add providers</option>
            </Select>
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="submit" loading={busy}>Send invite</Button>
            <Button type="button" variant="ghost" onClick={() => setInviteOpen(false)}>Cancel</Button>
          </div>
        </form>
      </Sheet>
    </div>
  );
}
